import type { TagTone } from '../Badge/Badge';

/**
 * THE SHAPE EVERY CHART READS, AND THE GEOMETRY THEY ALL SHARE
 * (`docs/decisions/reports.md` T1 and T2, `reports-charting-research.md` section 5).
 *
 * One data shape for the whole family, close to what `POST /api/group` already returns:
 * a group is a KEY, a LABEL and a number. The key is the identity - a bucket's start in
 * ISO, or a select option's id - and the label is what a human reads; a chart that keyed
 * on the label would merge two options that happen to be spelled the same, which is the
 * rule D13 states for the wire and it holds here for the same reason.
 *
 * `null` IS NOT ZERO. A month with no closed deals and a month whose number we could not
 * read are different sentences, and only one of them is a quantity. A null point draws no
 * mark, leaves a gap in a line rather than a dip to the floor, and reads as "No data" in
 * the hidden table.
 */
export interface ChartPoint {
  /** The identity: a bucket start in ISO, an option id. Never the label. */
  readonly key: string;
  /** What a human reads. May be long; the charts truncate for paint, never for the table. */
  readonly label: string;
  /** `null` means "no data", never zero. */
  readonly value: number | null;
  /**
   * A select option's own colour, where the point IS an option (a lifecycle stage, a
   * package). Donut slices and funnel stages carry it; in a bar or line the SERIES carries
   * the colour instead, because there the point is a bucket rather than a category.
   */
  readonly tone?: TagTone | undefined;
}

export interface ChartSeries {
  readonly id: string;
  readonly label: string;
  /** The series' own colour where it is a select option. Otherwise the palette decides. */
  readonly tone?: TagTone | undefined;
  readonly points: readonly ChartPoint[];
}

/**
 * THE COLOUR A MARK IS DRAWN IN, and the list is closed (`reports.md` T2, as amended by
 * the owner on 2026-09-06).
 *
 * THREE CASES, IN ORDER, and the order is the whole rule:
 *
 * 1. THE DATA'S OWN COLOUR. When the grouped field is a SELECT whose options carry
 *    colours - health red/yellow/green, a lifecycle stage - the series or the slice takes
 *    THAT colour. The product already shows that meaning in a badge on the table beside
 *    the chart, and a chart that recoloured it would be the product saying two different
 *    things about one fact.
 * 2. THE ACCENT, for the single series in a single-series chart and for the focused point.
 *    DESIGN.md allows exactly one accent and this is what it is for.
 * 3. THE ORDERED RAMP, `--color-chart-1..6`, for a grouping with no colour of its own:
 *    months, packages without option tones, plain counts. Six slots that mean nothing
 *    except "the first one, the second one", which is exactly what an arbitrary series is.
 *
 * WHAT CASE 3 IS NOT: the six SEMANTIC tag tones. Those mean success, warning and danger
 * everywhere else in the product, so a fourth series drawn in `danger` would be red for no
 * reason a reader could act on. That was the first version of this file and the owner
 * replaced it; the ramp is in `tokens.css` with its measured contrast.
 */
export type SeriesColor = 'highlight' | TagTone | ChartSlot;

/** The ordered ramp's six slots. Ordinal, never semantic. */
export type ChartSlot = 'chart-1' | 'chart-2' | 'chart-3' | 'chart-4' | 'chart-5' | 'chart-6';

export const CHART_SLOTS: readonly ChartSlot[] = [
  'chart-1',
  'chart-2',
  'chart-3',
  'chart-4',
  'chart-5',
  'chart-6',
];

/** Tailwind classes rather than inline styles, so the colour is a token and nothing else. */
const FILL: Record<SeriesColor, string> = {
  highlight: 'fill-accent',
  neutral: 'fill-tag-neutral',
  accent: 'fill-tag-accent',
  success: 'fill-tag-success',
  warning: 'fill-tag-warning',
  danger: 'fill-tag-danger',
  info: 'fill-tag-info',
  'chart-1': 'fill-chart-1',
  'chart-2': 'fill-chart-2',
  'chart-3': 'fill-chart-3',
  'chart-4': 'fill-chart-4',
  'chart-5': 'fill-chart-5',
  'chart-6': 'fill-chart-6',
};

const STROKE: Record<SeriesColor, string> = {
  highlight: 'stroke-accent',
  neutral: 'stroke-tag-neutral',
  accent: 'stroke-tag-accent',
  success: 'stroke-tag-success',
  warning: 'stroke-tag-warning',
  danger: 'stroke-tag-danger',
  info: 'stroke-tag-info',
  'chart-1': 'stroke-chart-1',
  'chart-2': 'stroke-chart-2',
  'chart-3': 'stroke-chart-3',
  'chart-4': 'stroke-chart-4',
  'chart-5': 'stroke-chart-5',
  'chart-6': 'stroke-chart-6',
};

/** The legend swatch and the funnel's bar are DOM, not SVG, so they need a background. */
const BACKGROUND: Record<SeriesColor, string> = {
  highlight: 'bg-accent',
  neutral: 'bg-tag-neutral',
  accent: 'bg-tag-accent',
  success: 'bg-tag-success',
  warning: 'bg-tag-warning',
  danger: 'bg-tag-danger',
  info: 'bg-tag-info',
  'chart-1': 'bg-chart-1',
  'chart-2': 'bg-chart-2',
  'chart-3': 'bg-chart-3',
  'chart-4': 'bg-chart-4',
  'chart-5': 'bg-chart-5',
  'chart-6': 'bg-chart-6',
};

export function fillClass(color: SeriesColor): string {
  return FILL[color];
}

export function strokeClass(color: SeriesColor): string {
  return STROKE[color];
}

export function backgroundClass(color: SeriesColor): string {
  return BACKGROUND[color];
}

export function seriesColor(tone: TagTone | undefined, index: number, total: number): SeriesColor {
  if (tone !== undefined) return tone;
  if (total <= 1) return 'highlight';
  return CHART_SLOTS[index % CHART_SLOTS.length] ?? 'chart-1';
}

/**
 * THE GEOMETRY CONSTANTS, in one place and with the reason they are numbers at all.
 *
 * DESIGN.md says tokens only, and every COLOUR here is one. Layout maths cannot be: an SVG
 * has to place a tick at an x in user units, and a CSS variable is not readable from
 * JavaScript without a `getComputedStyle` per frame. So the axis labels are PAINTED with
 * the token class `text-2xs` and these constants only RESERVE space for them, generously.
 * If the token ever grows, the reservation is too small and the gallery's measure step
 * reports the clipped text - which is the failure mode we want, rather than a silent one.
 *
 * `AXIS_CHAR_WIDTH` is a deliberate OVER-estimate of the average advance width of the UI
 * face (0.62em against ~0.5em for digits and lower case in SF Pro Text): over-reserving
 * truncates a label one character early, under-reserving overflows the box.
 *
 * THE RESERVATION IS ONE STEP LARGER THAN THE PAINT, SINCE E2. An axis label is painted
 * with `text-caption`, the 12px Caption 1 token DESIGN.md's first criterion names for
 * exactly this job ("a chart axis label"), and `spec/screens/reports.md` section 2 item 4
 * asks for it by name. This constant stays at 13, which is the old paint size: the whole
 * point of the estimate is to over-reserve, and reserving 13px of room for 12px of text
 * keeps every error on the safe side of the box.
 */
export const AXIS_FONT_PX = 13;
export const AXIS_LINE_PX = 18;
export const AXIS_CHAR_WIDTH = 0.62 * AXIS_FONT_PX;
/** Breathing room between the plot and the labels around it. */
export const AXIS_GAP_PX = 6;
/** The gap the plot keeps at the top so the tallest bar is not flush with the frame. */
export const PLOT_PAD_PX = 8;
/** No value gutter may eat more than this share of a narrow container. */
export const MAX_GUTTER_SHARE = 0.35;
/** What a chart draws into before a `ResizeObserver` has measured its container. */
export const FALLBACK_WIDTH_PX = 640;

/** A conservative estimate of how wide a string paints at the axis size. */
export function estimateTextWidth(text: string): number {
  return text.length * AXIS_CHAR_WIDTH;
}

/** How many characters fit in `width`, never fewer than zero. */
export function charsThatFit(width: number): number {
  return Math.max(0, Math.floor(width / AXIS_CHAR_WIDTH));
}

/**
 * Truncate for PAINT only. The full label is always in the hidden table and in the
 * tooltip, so nothing is lost - and a label that runs out of its box would be reported by
 * the measure step as clipped text, which is the same information a frame later.
 */
export function truncateLabel(label: string, available: number): string {
  const fits = charsThatFit(available);
  if (fits >= label.length) return label;
  if (fits < 2) return '';
  return `${label.slice(0, fits - 1).trimEnd()}…`;
}

/**
 * HOW WIDE A CENTRED LABEL MAY PAINT WITHOUT LEAVING ITS BOX.
 *
 * A label centred on its category may not reach past its neighbours (that is `span`) and
 * may not reach past the edges of the drawing either - and the second bound is the one a
 * chart forgets. With thinned ticks the first label's span is several categories wide
 * while its centre is only half a category from the left edge, so a label sized by span
 * alone paints outside the SVG, where it is clipped without a word. Twice the distance to
 * the nearer edge is the honest budget, because the label grows both ways from its centre.
 */
export function labelBudget(centre: number, min: number, max: number, span: number): number {
  return Math.max(0, Math.min(span, 2 * (centre - min), 2 * (max - centre)));
}

/**
 * Which category indices get a label. Labels are THINNED, never rotated: rotated axis text
 * is unreadable on a phone and it changes the height of the plot, so a chart that rotates
 * at 375 is a different chart from the one at 1440.
 */
export function labelStep(count: number, plotWidth: number, minLabelPx: number): number {
  if (count <= 1) return 1;
  const perCategory = plotWidth / count;
  if (perCategory <= 0) return count;
  return Math.max(1, Math.ceil(minLabelPx / perCategory));
}

/**
 * The value axis: a domain that ALWAYS includes zero, because a bar chart whose baseline
 * is not zero lies about the ratio between its bars, and a rounded top so the ticks are
 * numbers a reader recognises.
 */
export function valueDomain(values: readonly number[]): readonly [number, number] {
  const finite = values.filter((value) => Number.isFinite(value));
  const min = Math.min(0, ...finite);
  const max = Math.max(0, ...finite);
  // An all-zero (or empty) series still needs a scale with a width, or every mark lands on
  // the same pixel and the axis prints "0" three times.
  if (min === 0 && max === 0) return [0, 1];
  return [min, max];
}

/** Evenly spaced ticks across the domain, ends included, and always through zero. */
export function valueTicks(domain: readonly [number, number], count: number): readonly number[] {
  const [min, max] = domain;
  const steps = Math.max(1, count);
  const ticks: number[] = [];
  for (let index = 0; index <= steps; index += 1) {
    ticks.push(min + ((max - min) * index) / steps);
  }
  if (min < 0 && max > 0 && !ticks.includes(0)) ticks.push(0);
  return ticks;
}

const DEFAULT_FORMAT = new Intl.NumberFormat();

/** The default number formatter, so every chart prints numbers the same way. */
export function formatValue(value: number): string {
  return DEFAULT_FORMAT.format(value);
}

/** The sentence a null point reads as, everywhere: the table, the tooltip, the announcement. */
export const NO_DATA = 'No data';

/**
 * The categories a multi-series chart plots against, in the order the FIRST series gives
 * them, with anything only a later series knows about appended. The group read returns its
 * buckets in order and every series shares them; a chart that sorted its own axis would
 * disagree with the table beside it.
 */
export function categoriesOf(series: readonly ChartSeries[]): readonly ChartPoint[] {
  const seen = new Map<string, ChartPoint>();
  for (const entry of series) {
    for (const point of entry.points) {
      if (!seen.has(point.key)) seen.set(point.key, point);
    }
  }
  return [...seen.values()];
}

/** One series' value at a category, or `null` where that series has no point there. */
export function valueAt(series: ChartSeries, key: string): number | null {
  return series.points.find((point) => point.key === key)?.value ?? null;
}
